import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Phone, Mail, MapPin, Truck, MessageCircle, X, Clock, XCircle, Copy, Check, Calendar, ExternalLink } from "lucide-react";
import { useCart } from "../context/CartContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const formatRub = (n) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(n) + " ₽";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const months = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()} г., ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
};

export default function OrderSuccess() {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const phone = params.get("phone") || "";
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => { clearCart(); }, [clearCart]);

  useEffect(() => {
    if (!orderNumber) return;
    let cancelled = false;

    const fetchOrder = async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/orders/${encodeURIComponent(orderNumber)}?phone=${encodeURIComponent(phone)}`
        );
        if (!res.ok) throw new Error("load");
        const data = await res.json();
        if (cancelled) return;
        setOrder(data);
        if (data.paymentStatus !== "pending" && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {
        if (!cancelled) setError("Не удалось загрузить заказ");
      }
    };

    fetchOrder();
    pollRef.current = setInterval(fetchOrder, 3000);
    const stop = setTimeout(() => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 120_000);

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      clearTimeout(stop);
    };
  }, [orderNumber, phone]);

  const handleCopy = () => {
    navigator.clipboard.writeText(orderNumber).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  if (error) {
    return (
      <div className="min-h-[100dvh] bg-[#F5F0EB] flex flex-col items-center justify-center px-5 gap-4" data-testid="order-error">
        <p className="text-[#1A1A1A]/55 text-sm font-sans">{error}</p>
        <button
          onClick={() => navigate("/")}
          className="px-8 py-3 bg-[#1A1A1A] text-white text-sm font-sans font-medium rounded-full"
        >
          На главную
        </button>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-[100dvh] bg-[#F5F0EB] flex items-center justify-center" data-testid="order-loading">
        <div className="w-6 h-6 border-2 border-[#1A1A1A]/15 border-t-[#1A1A1A] rounded-full animate-spin" />
      </div>
    );
  }

  const isPvz = order.delivery?.method === "pvz";
  const paymentStatus = order.paymentStatus;
  const hero = (() => {
    if (paymentStatus === "paid") {
      return {
        Icon: CheckCircle2, color: "text-emerald-500",
        title: "Спасибо!", sub: "Ваш заказ принят",
        detail: "Мы уже начали обрабатывать ваш заказ.",
      };
    }
    if (paymentStatus === "failed" || paymentStatus === "cancelled") {
      return {
        Icon: XCircle, color: "text-red-500",
        title: "Оплата не прошла", sub: "Заказ создан",
        detail: "Вы можете оплатить позже — мы свяжемся с вами.",
      };
    }
    return {
      Icon: Clock, color: "text-amber-500",
      title: "Ожидание оплаты", sub: "Заказ создан",
      detail: "Подтверждение поступит в течение нескольких минут",
    };
  })();
  const HeroIcon = hero.Icon;

  return (
    <div className="min-h-[100dvh] bg-[#F5F0EB] flex flex-col" data-testid="order-success-page">
      <div className="w-full max-w-[480px] mx-auto flex flex-col flex-1">
      <header className="px-5 pt-3 pb-2 flex items-center justify-between">
        <div className="w-10" />
        <h1 className="text-[18px] font-serif font-bold text-[#1A1A1A]">Готово</h1>
        <button
          onClick={() => navigate("/")}
          className="w-10 h-10 flex items-center justify-center -mr-2"
          data-testid="success-home"
        >
          <X size={22} className="text-[#1A1A1A]" strokeWidth={1.5} />
        </button>
      </header>

      <div className="px-5 flex flex-col gap-4 pb-10">
        {/* Hero */}
        <div
          className="bg-white rounded-2xl p-6 flex flex-col items-center text-center"
          data-testid="success-hero"
          data-payment-status={paymentStatus}
        >
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 ${paymentStatus === "paid" ? "bg-emerald-500/10" : paymentStatus === "failed" ? "bg-red-500/10" : "bg-amber-500/10"}`}>
            <HeroIcon size={36} className={hero.color} strokeWidth={1.6} />
          </div>
          <h2 className="text-[24px] font-serif font-bold text-[#1A1A1A] mb-1">{hero.title}</h2>
          <p className="text-[15px] text-[#1A1A1A]/65 font-sans">{hero.sub}</p>
          <p className="text-[13px] text-[#1A1A1A]/45 font-sans mt-1">{hero.detail}</p>
        </div>

        {/* Order number */}
        <div className="bg-white rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] text-[#1A1A1A]/45 font-sans">Номер заказа</p>
              <p className="text-[16px] font-sans font-bold text-[#1A1A1A] mt-0.5" data-testid="success-order-number">
                {order.orderNumber}
              </p>
            </div>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#F5F0EB] text-[12px] font-sans text-[#1A1A1A]/65 active:bg-[#E8E3DE] transition-colors"
            >
              {copied ? <Check size={14} strokeWidth={2} className="text-emerald-600" /> : <Copy size={14} strokeWidth={1.6} />}
              {copied ? "Скопировано" : "Скопировать"}
            </button>
          </div>
        </div>

        {/* Info rows */}
        <div className="bg-white rounded-2xl p-4 space-y-3">
          <div className="flex items-center gap-3">
            <Calendar size={16} strokeWidth={1.5} className="text-[#1A1A1A]/45 shrink-0" />
            <div>
              <p className="text-[11px] text-[#1A1A1A]/45 font-sans">Дата заказа</p>
              <p className="text-[13px] font-sans text-[#1A1A1A]">{formatDate(order.createdAt)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Mail size={16} strokeWidth={1.5} className="text-[#1A1A1A]/45 shrink-0" />
            <div>
              <p className="text-[11px] text-[#1A1A1A]/45 font-sans">Подтверждение отправлено на</p>
              <p className="text-[13px] font-sans text-[#1A1A1A]">{order.customer?.email}</p>
            </div>
          </div>
        </div>

        {/* Delivery */}
        <section className="bg-white rounded-2xl p-4" data-testid="success-delivery">
          <h3 className="text-[13px] font-sans font-semibold text-[#1A1A1A] mb-3 flex items-center gap-2">
            Доставка
            <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#4DB45E]/15 text-[#4DB45E] font-bold">СДЭК</span>
          </h3>

          <div className="rounded-xl bg-[#F5F0EB] p-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#4DB45E]/15 flex items-center justify-center shrink-0 mt-0.5">
                {isPvz ? <MapPin size={16} className="text-[#4DB45E]" strokeWidth={2} /> : <Truck size={16} className="text-[#4DB45E]" strokeWidth={2} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-[#1A1A1A]/45 font-sans font-medium">
                  {isPvz ? "ПВЗ СДЭК" : "Курьерская доставка"}
                </p>
                <p className="text-[13px] font-sans text-[#1A1A1A] mt-0.5">
                  {isPvz ? (order.delivery.pvzAddress || "ПВЗ будет назначен") : `${order.delivery.city || ""}${order.delivery.address ? ", " + order.delivery.address : ""}`}
                </p>
                <div className="flex items-center gap-1 mt-1.5">
                  <Clock size={12} className="text-[#1A1A1A]/40" strokeWidth={1.6} />
                  <p className="text-[11px] text-[#1A1A1A]/55 font-sans">Срок доставки: 14-20 дней</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Track order */}
        <button
          onClick={() => {}}
          className="w-full bg-white rounded-2xl p-4 flex items-center justify-between text-left"
        >
          <span className="text-[14px] font-sans font-medium text-[#1A1A1A]">Отследить заказ</span>
          <ExternalLink size={18} className="text-[#1A1A1A]/40" strokeWidth={1.6} />
        </button>

        {/* Help */}
        <section className="bg-white rounded-2xl p-4">
          <h3 className="text-[13px] font-sans font-semibold text-[#1A1A1A] mb-2">Нужна помощь?</h3>
          <a
            href="https://wa.me/79165479373"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-3 rounded-full bg-emerald-500/10 text-emerald-700 text-[13px] font-sans font-medium"
            data-testid="success-whatsapp"
          >
            <MessageCircle size={16} strokeWidth={1.8} />
            Написать в WhatsApp
          </a>
        </section>
      </div>
      </div>
    </div>
  );
}
